import { useEffect, useRef } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { useToast } from "../../ui";
import { useNextT } from "../../i18n";
import { useCurrentUser, useIsImpersonatingSession, useWorkspaceId } from "../../bridges/session";
import { isApiError, toApiError } from "../../data/errors";
import { postReport, refreshContact, refreshInbox } from "../api";
import { clearDraft, failureMark, finishSending, isDraftHeld, isDraftSending, listDrafts, loadDraft, markReportSaved, markSending, purgeForeignDrafts, ReportDraft, saveDraft, whenDraftFree } from "../draft";

/**
 * F5 (Q3, owner answer 9): while the app is open, a report draft whose save failed for want of a connection is sent
 * again once when the connection comes back, with its own key and the exact body that failed (so a first save that
 * did arrive is not filed twice). Saved → "Report saved" and the draft is removed; refused → it stays a draft with the
 * reason, for the next time the report is opened. A draft open in a report form (in this tab, or in another tab: its
 * Web Lock) is left to that form, and a form opened while it is on its way waits for it (`markSending`). On load it also removes other users' drafts from this
 * device. While viewing as someone it does nothing (their drafts are not kept, the admin's stay). Render once,
 * inside a ToastProvider; renders nothing.
 */
export const ReportDraftResender = () => {
  const { t } = useNextT();
  const toast = useToast();
  const client = useQueryClient();
  const userId = useCurrentUser()?.id ?? null;
  const ws = useWorkspaceId();
  const impersonating = useIsImpersonatingSession();
  const latest = useRef({ t, toast, client });
  latest.current = { t, toast, client };

  useEffect(() => { if (userId !== null && !impersonating) purgeForeignDrafts(userId); }, [userId, impersonating]);

  useEffect(() => {
    if (userId === null || ws === null || impersonating) return undefined;
    /** Sends one draft again if it still asks for it (it is read again: another tab may have changed it meanwhile). */
    const sendOne = (key: string, householdId: number, seen: ReportDraft): Promise<unknown> | void => {
      const draft = loadDraft(key);
      if (!draft || draft.savedAt !== seen.savedAt) return undefined;
      if (draft.failed !== "network" || draft.resent || !draft.idempotencyKey || !draft.body) return undefined;
      if (isDraftHeld(key) || isDraftSending(key)) return undefined;
      let wire: Record<string, unknown>;
      try { wire = JSON.parse(draft.body) as Record<string, unknown>; } catch { return undefined; }
      // Marked first (synchronously): a second listener, a second "online" or a form opened now never sends it again.
      markSending(key);
      saveDraft(key, { ...draft, resent: true });
      return postReport(householdId, wire, draft.idempotencyKey).then((result) => {
        markReportSaved(key);
        clearDraft(key);
        finishSending(key, "saved");
        const { t: tr, toast: show, client: qc } = latest.current;
        void Promise.all([refreshContact(qc, ws, householdId), refreshInbox(qc, ws)]);
        show({ message: tr(result.requests.some((r) => r.status === "pending") ? "visits.report.saved_sent" : "visits.report.saved") });
      }, (err) => {
        const e = isApiError(err) ? err : toApiError(err);
        if (e.code === "IDEMPOTENCY_KEY_REUSED") {
          // Its key already went with another body: that report is on the server. Never filed again.
          markReportSaved(key);
          clearDraft(key);
          finishSending(key, "saved");
          const { t: tr, toast: show, client: qc } = latest.current;
          void refreshContact(qc, ws, householdId);
          show({ message: tr("visits.errors.idempotency_key_reused") });
          return;
        }
        const now = loadDraft(key);
        if (now) saveDraft(key, { ...now, failed: undefined, errorCode: undefined, errorStatus: undefined, fieldKeys: undefined, ...failureMark(e), resent: true });
        finishSending(key, "kept");
      });
    };
    const back = () => {
      listDrafts(userId, ws).forEach(({ key, householdId, draft }) => {
        if (draft.failed !== "network" || draft.resent || !draft.idempotencyKey || !draft.body) return;
        if (isDraftHeld(key) || isDraftSending(key)) return;
        // A form open on it in another tab sends it itself: skipped (the lock is not free).
        whenDraftFree(key, () => sendOne(key, householdId, draft));
      });
    };
    window.addEventListener("online", back);
    return () => window.removeEventListener("online", back);
  }, [userId, ws, impersonating]);

  return null;
};
